import {
  CancelAllTasksParams,
  ListTasksParams,
} from '@/pages/tools/RevaluationTool/types';
import { RevalueTradesMetricsRequestPayload } from '@/pages/tools/RevaluationTool/types';
import { AxiosInstance } from 'axios';

export default class RevalueTradesAPI {
  client: AxiosInstance;
  constructor(client: AxiosInstance) {
    this.client = client;
  }

  /**
   * Lists background revaluation tasks, most-recently-updated first, so the UI
   * can discover runs it didn't start (e.g. after the modal was closed).
   * `active=true` (default) returns only in-flight runs; `active=false` also
   * includes finished history.
   */
  async listTasks(params: ListTasksParams = {}) {
    return this.client.get('/v2/admin/tasks/', { params });
  }

  async revalueTransactionValueMetrics(
    payload: RevalueTradesMetricsRequestPayload,
  ) {
    return this.client.post(
      '/v2/admin/revalue-transaction-value-metrics/',
      payload,
    );
  }

  async revalueTradesBCVA(payload: RevalueTradesMetricsRequestPayload) {
    return this.client.post('/v2/admin/revalue-transaction-bcva/', payload);
  }

  async getTaskProgress(taskId: string) {
    return this.client.get(`/v2/admin/task/${taskId}/`);
  }

  /**
   * Absolute URL for the SSE stream of the *task listing* — the streaming
   * equivalent of `listTasks`. On connect it pushes the current snapshot, then a
   * fresh full list on every change (new run, progress tick, completion,
   * cancellation), so the bell stays live without polling. Accepts the same
   * query params as `listTasks` (active / revalue_type / limit). Auth is
   * session-cookie based — open the stream with credentials included.
   */
  getTasksStreamUrl(params: ListTasksParams = {}) {
    const baseURL = this.client.defaults.baseURL ?? '';
    const query = new URLSearchParams();
    if (params.active !== undefined) query.set('active', String(params.active));
    if (params.revalue_type) query.set('revalue_type', params.revalue_type);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    const qs = query.toString();
    return `${baseURL}/v2/admin/tasks/stream/${qs ? `?${qs}` : ''}`;
  }

  /**
   * Absolute URL for the SSE progress stream. EventSource can't go through the
   * axios instance, so we build the full URL from the client's configured base
   * (auth is session-cookie based — open the stream with `withCredentials: true`).
   */
  getTaskStreamUrl(taskId: string) {
    const baseURL = this.client.defaults.baseURL ?? '';
    return `${baseURL}/v2/admin/task/${taskId}/stream/`;
  }

  async cancelTask(taskId: string) {
    return this.client.post(`/v2/admin/task/${taskId}/cancel/`, {});
  }

  /**
   * Cancels all of the current user's queued + running runs (and clears dead
   * orphans). Pass `revalue_type` to scope the cancel to one run type; an empty
   * body cancels everything.
   */
  async cancelAllTasks(payload: CancelAllTasksParams = {}) {
    return this.client.post('/v2/admin/tasks/cancel/', payload);
  }

  async getReportDownload(taskId: string) {
    return this.client.get(`/v2/admin/task/${taskId}/download-report-url/`);
  }
}
